import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import StripePaginationResult from 'src/app/core/domain/stripe_pagination_result';
import { Invoice } from 'src/app/data/dietitian/domain/subscription/invoice';
import { SubscriptionCommands } from 'src/app/data/dietitian/domain/subscription/subscription_commands';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import {
  DietitianState,
  DietitianStateProvider,
} from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { Feature } from '../../../../../../data/dietitian/domain/subscription/feature';
import { PaymentMethod } from '../../../../../../data/dietitian/domain/subscription/payment_method';
import { SubscriptionFeature } from '../../../../../../data/dietitian/domain/subscription/subscription_feature';
import { StripeProduct } from '../../../../../../data/dietitian/repositories/subscription_repository';
import {
  StripeBalanceModel,
  SubscriptionModel,
} from './account-subscription.component';

@Injectable()
export class AccountSubscriptionFacade {
  constructor(
    private subscriptionCommands: SubscriptionCommands,
    private dietitianStateProvider: DietitianStateProvider,
  ) {
    // do nothing
  }

  get dietitian(): Dietitian | undefined {
    return this.dietitianStateProvider.state.entity;
  }

  get dietitianState$(): Observable<DietitianState> {
    return this.dietitianStateProvider.state$;
  }

  geLatestSubscription(
    dietitian: Dietitian,
  ): Promise<SubscriptionModel | undefined> {
    return this.subscriptionCommands.getLatestSubscription(dietitian);
  }

  createSubscriptionCheckoutSession(
    priceId: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<string> {
    return this.subscriptionCommands.createSubscriptionCheckoutSession(
      priceId,
      successUrl,
      cancelUrl,
    );
  }

  createUpdatePaymentMethodCheckoutSession(
    successUrl: string,
    cancelUrl: string,
  ): Promise<string> {
    return this.subscriptionCommands.createUpdatePaymentMethodCheckoutSession(
      successUrl,
      cancelUrl,
    );
  }

  cancelSubscriptions(): Promise<void> {
    return this.subscriptionCommands.cancelSubscriptions();
  }

  async getInvoices(
    pageSize: number,
    startingAfter: string | null,
    endingBefore: string | null,
  ): Promise<StripePaginationResult<Invoice>> {
    return this.subscriptionCommands.getInvoices(
      pageSize,
      startingAfter,
      endingBefore,
    );
  }

  async getBalances(): Promise<StripeBalanceModel[]> {
    return this.subscriptionCommands.getBalances();
  }

  async getFeatures(): Promise<Feature[]> {
    return this.subscriptionCommands.getFeatures();
  }

  async getStripeProduct(stripeProductId: string): Promise<StripeProduct> {
    return this.subscriptionCommands.getStripeProduct(stripeProductId);
  }

  async getRunningSubscriptionFeature(dietitian: Dietitian, feature: Feature) {
    return this.subscriptionCommands.getRunningSubscriptionFeature(
      dietitian,
      feature,
    );
  }

  async cancelSubscriptionFeature(subscriptionFeature: SubscriptionFeature) {
    return this.subscriptionCommands.cancelSubscriptionFeature(
      subscriptionFeature,
    );
  }

  async updatePremium(stripeSubscriptionId: string, stripePriceId: string) {
    return this.subscriptionCommands.updatePremium(
      stripeSubscriptionId,
      stripePriceId,
    );
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    return this.subscriptionCommands.getPaymentMethods();
  }

  async subscribeFeature(
    featurePriceId: string | undefined,
    coupon: string | undefined,
  ) {
    if (featurePriceId) {
      return this.subscriptionCommands.subscribeFeature(featurePriceId, coupon);
    }
    return;
  }

  async defaultPaymentMethod(paymentMethodId: string) {
    return this.subscriptionCommands.defaultPaymentMethod(paymentMethodId);
  }

  async loadFeature(id: string): Promise<SubscriptionFeature> {
    return this.subscriptionCommands.loadFeature(id);
  }

  subscriptionFeatureValueChanges(subscriptionFeatureId: string) {
    return this.subscriptionCommands.subscriptionFeatureValueChanges(
      subscriptionFeatureId,
    );
  }

  async getLastInvoiceBySubscription(stripeSubscriptionId: string | undefined) {
    return this.subscriptionCommands.getLastInvoiceBySubscription(
      stripeSubscriptionId,
    );
  }
}
